"use client";

import { usePathname } from "@/lib/navigation";
import { withSentry } from "@/lib/sentry-browser";
import { useEffect } from "react";
import type { PortalRole } from "@/lib/portal-auth-shared";
import { portalPageDefinitions } from "@/lib/portal-pages";

// Use a fixed vocabulary, never article slugs, chat IDs, or query parameters.
const portalPages = new Set(
  ["notes", ...portalPageDefinitions.map((page) => page.href.split("/")[1])],
);

export function portalTelemetryLabels(pathname: string) {
  const path = pathname.split(/[?#]/, 1)[0];
  const segment = path.split("/")[1] ?? "";
  if (segment === "admin" || segment === "dev") {
    return { surface: segment, page: segment };
  }
  if (path === "/dashboard/support") return { surface: "portal", page: "support" };
  if (path === "/dashboard/billing") return { surface: "portal", page: "billing" };
  if (!segment || segment === "dashboard") return { surface: "portal", page: "home" };
  return { surface: "portal", page: portalPages.has(segment) ? segment : "other" };
}

/** Attribute operational errors without exporting the member's profile. */
export function PortalTelemetry({
  memberId,
  role = "public",
}: {
  memberId?: string;
  role?: PortalRole | "public";
}) {
  const pathname = usePathname();

  useEffect(() => {
    withSentry((sentry) => {
      sentry.setUser(memberId ? { id: memberId } : null);
      sentry.setTag("member.role", role);
    });
    return () => withSentry((sentry) => {
      sentry.setUser(null);
      sentry.setTag("member.role", undefined);
    });
  }, [memberId, role]);

  useEffect(() => {
    const { surface, page } = portalTelemetryLabels(pathname ?? "/");
    withSentry((sentry) => {
      sentry.setTag("app.surface", surface);
      sentry.setTag("app.page", page);
    });
    return () => withSentry((sentry) => {
      sentry.setTag("app.surface", undefined);
      sentry.setTag("app.page", undefined);
    });
  }, [pathname]);

  return null;
}
